
import { useState, useEffect } from 'react';
import { Sparkles, Loader2, CheckCircle } from 'lucide-react';

interface AIResumeAnalysisProps {
  file: File | null;
}

const AIResumeAnalysis = ({ file }: AIResumeAnalysisProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisCompleted, setAnalysisCompleted] = useState(false);
  const [summary, setSummary] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('Starting analysis...');

  useEffect(() => {
    if (file) {
      startAnalysis();
    }
  }, [file]);

  const startAnalysis = () => {
    setIsAnalyzing(true);
    setAnalysisCompleted(false);
    setProgress(0);
    setStage('Starting analysis...');

    // Simulating AI analysis with a sequence of updates
    const duration = 5000; // 5 seconds total
    const interval = 100; // Update every 100ms
    const totalSteps = duration / interval;
    let step = 0;

    const stages = [
      'Starting analysis...',
      'Extracting text from document...',
      'Analyzing skills and experience...',
      'Evaluating qualifications...',
      'Generating summary...',
      'Analysis complete'
    ];

    const timer = setInterval(() => {
      step++;
      const newProgress = Math.min(Math.round((step / totalSteps) * 100), 100);
      setProgress(newProgress);

      // Update stage at appropriate progress points
      if (newProgress > 15 && newProgress <= 30) setStage(stages[1]);
      else if (newProgress > 30 && newProgress <= 50) setStage(stages[2]);
      else if (newProgress > 50 && newProgress <= 70) setStage(stages[3]);
      else if (newProgress > 70 && newProgress <= 90) setStage(stages[4]);
      else if (newProgress > 90) setStage(stages[5]);

      if (newProgress === 100) {
        clearInterval(timer);
        setTimeout(() => {
          setIsAnalyzing(false);
          setAnalysisCompleted(true);
          // Set mock analysis results
          setSummary(
            "Experienced software engineer with 5+ years of expertise in React, Node.js, and cloud technologies. Demonstrated success leading teams and delivering high-performance applications. Strong problem-solving abilities and excellent communication skills. Previous experience at tech startups and enterprise companies shows versatility and adaptability."
          );
          setSkills([
            'React.js',
            'Node.js',
            'TypeScript',
            'AWS',
            'Docker',
            'MongoDB',
            'UI/UX Design',
            'Team Leadership',
            'Agile Methodologies',
            'CI/CD Pipelines',
            'System Architecture'
          ]);
        }, 500);
      }
    }, interval);
  };

  if (!file) {
    return null;
  }

  return (
    <div className="mt-6 border rounded-xl overflow-hidden">
      {isAnalyzing ? (
        <div className="p-5">
          <div className="flex items-center mb-3">
            <Sparkles className="text-apple-blue mr-2" size={18} />
            <h3 className="text-lg font-medium text-apple-text">AI Resume Analysis</h3>
          </div>
          
          <div className="flex items-center mb-2">
            <Loader2 className="animate-spin mr-3 text-apple-blue" size={20} />
            <span className="text-apple-text">{stage}</span>
          </div>
          
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
            <div 
              className="h-full bg-apple-blue transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <p className="text-sm text-apple-text-secondary">
            Our AI is analyzing your resume to extract key information and generate a summary.
          </p>
        </div>
      ) : analysisCompleted ? (
        <div className="p-5">
          <div className="flex items-center mb-4">
            <Sparkles className="text-apple-blue mr-2" size={18} />
            <h3 className="text-lg font-medium text-apple-text">AI Resume Analysis</h3>
          </div>
          
          <div className="mb-5">
            <h4 className="font-medium text-apple-text mb-2">Summary</h4>
            <p className="text-apple-text">{summary}</p>
          </div>
          
          <div>
            <h4 className="font-medium text-apple-text mb-2">Key Skills</h4>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill, index) => (
                <div 
                  key={index} 
                  className="bg-blue-50 text-apple-blue px-3 py-1 rounded-full text-sm"
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-5 pt-5 border-t border-gray-100 flex items-center text-sm text-apple-text-secondary">
            <CheckCircle className="text-green-500 mr-2" size={16} />
            Analysis completed successfully
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AIResumeAnalysis;
